// Student Service - API calls for student panel
import { useAuthStore } from '@/features/auth/stores/authStore';
import {
  GetStudentClassesQueryDto,
  StudentClassInfo,
  StudentClassDetail,
  StudentGradeBreakdown,
  StudentAttendanceHistory,
  ClassmateInfo,
  StudentAnnouncementResponse,
  StudentSyllabusResponse,
  StudentCourseContentResponse,
  StudentAssignmentResponse,
  StudentAssignmentDetail,
  StudentSubmissionResponse,
  SubmitAssignmentDto,
  UpdateSubmissionDto,
  StudentExamScheduleResponse,
  StudentRattrapageScheduleResponse,
  StudentRattrapageHistoryResponse,
  StudentAcademicYear,
} from '@/types/student.types';

const API_BASE = `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:4000'}/api`;

const getHeaders = () => {
  const tokens = useAuthStore.getState().tokens;
  const accessToken = tokens?.accessToken;
  return {
    'Content-Type': 'application/json',
    Authorization: accessToken ? `Bearer ${accessToken}` : '',
  };
};

// Optional past-year suffix shared by every per-course read (absent = current year)
const withYear = (academicYearId?: string): string =>
  academicYearId ? `?academicYearId=${encodeURIComponent(academicYearId)}` : '';

// Year + course-instance suffix: `groupeId` names the former group's instance
// of a course when the element is taught in both the former and the current
// group (absent = current group).
const withYearAndGroup = (academicYearId?: string, groupeId?: string): string => {
  const params = new URLSearchParams();
  if (academicYearId) params.append('academicYearId', academicYearId);
  if (groupeId) params.append('groupeId', groupeId);
  const qs = params.toString();
  return qs ? `?${qs}` : '';
};

// ==================== ACADEMIC YEARS ====================

export const fetchStudentAcademicYears = async (): Promise<StudentAcademicYear[]> => {
  const response = await fetch(`${API_BASE}/student/academic-years`, {
    method: 'GET',
    headers: getHeaders(),
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to fetch academic years');
  }
  return response.json();
};

// ==================== MY CLASSES ====================

export const fetchMyClasses = async (
  query: GetStudentClassesQueryDto = {}
): Promise<StudentClassInfo[]> => {
  const params = new URLSearchParams();
  if (query.academicYearId) params.append('academicYearId', query.academicYearId);
  if (query.semester !== undefined) params.append('semester', String(query.semester));

  const url = `${API_BASE}/student/my-classes${params.toString() ? `?${params.toString()}` : ''}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: getHeaders(),
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to fetch classes');
  }
  return response.json();
};

export const fetchClassDetail = async (
  moduleElementId: string,
  academicYearId?: string,
  groupeId?: string
): Promise<StudentClassDetail> => {
  const url = `${API_BASE}/student/my-classes/${moduleElementId}${withYearAndGroup(academicYearId, groupeId)}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: getHeaders(),
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to fetch class detail');
  }
  return response.json();
};

// ==================== CONTENT (READ-ONLY) ====================

export const fetchCourseContent = async (
  moduleElementId: string,
  academicYearId?: string,
  groupeId?: string
): Promise<StudentCourseContentResponse> => {
  const response = await fetch(
    `${API_BASE}/student/module/${moduleElementId}/content${withYearAndGroup(academicYearId, groupeId)}`,
    {
      method: 'GET',
      headers: getHeaders(),
    }
  );
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to fetch course content');
  }
  return response.json();
};

export const fetchAnnouncements = async (
  moduleElementId: string,
  academicYearId?: string,
  groupeId?: string
): Promise<StudentAnnouncementResponse[]> => {
  const response = await fetch(
    `${API_BASE}/student/module/${moduleElementId}/announcements${withYearAndGroup(academicYearId, groupeId)}`,
    {
      method: 'GET',
      headers: getHeaders(),
    }
  );
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to fetch announcements');
  }
  return response.json();
};

export const fetchSyllabus = async (
  moduleElementId: string,
  academicYearId?: string,
  groupeId?: string
): Promise<StudentSyllabusResponse | null> => {
  const response = await fetch(
    `${API_BASE}/student/module/${moduleElementId}/syllabus${withYearAndGroup(academicYearId, groupeId)}`,
    {
      method: 'GET',
      headers: getHeaders(),
    }
  );
  if (!response.ok) {
    if (response.status === 404) {
      return null;
    }
    const error = await response.json();
    throw new Error(error.message || 'Failed to fetch syllabus');
  }
  // "No syllabus yet" is a 200 with an empty body, not an error
  const text = await response.text();
  return text ? (JSON.parse(text) as StudentSyllabusResponse) : null;
};

// ==================== GRADES ====================

export const fetchMyGrades = async (
  moduleElementId: string,
  academicYearId?: string,
  groupeId?: string
): Promise<StudentGradeBreakdown> => {
  const response = await fetch(
    `${API_BASE}/student/module/${moduleElementId}/my-grades${withYearAndGroup(academicYearId, groupeId)}`,
    {
      method: 'GET',
      headers: getHeaders(),
    }
  );
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to fetch grades');
  }
  return response.json();
};

// ==================== ATTENDANCE ====================

export const fetchMyAttendance = async (
  moduleElementId: string,
  academicYearId?: string,
  groupeId?: string
): Promise<StudentAttendanceHistory> => {
  const response = await fetch(
    `${API_BASE}/student/module/${moduleElementId}/my-attendance${withYearAndGroup(academicYearId, groupeId)}`,
    {
      method: 'GET',
      headers: getHeaders(),
    }
  );
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to fetch attendance');
  }
  return response.json();
};

// ==================== CLASSMATES ====================

export const fetchClassmates = async (
  groupeId: string
): Promise<ClassmateInfo[]> => {
  const response = await fetch(
    `${API_BASE}/student/groupe/${groupeId}/classmates`,
    {
      method: 'GET',
      headers: getHeaders(),
    }
  );
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to fetch classmates');
  }
  return response.json();
};

// ==================== ASSIGNMENTS ====================

export const fetchAssignments = async (
  moduleElementId: string,
  academicYearId?: string,
  groupeId?: string
): Promise<StudentAssignmentResponse[]> => {
  const response = await fetch(
    `${API_BASE}/student/module/${moduleElementId}/assignments${withYearAndGroup(academicYearId, groupeId)}`,
    {
      method: 'GET',
      headers: getHeaders(),
    }
  );
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to fetch assignments');
  }
  return response.json();
};

export const fetchAssignmentDetail = async (
  assignmentId: string,
  academicYearId?: string
): Promise<StudentAssignmentDetail> => {
  const response = await fetch(
    `${API_BASE}/student/assignments/${assignmentId}${withYear(academicYearId)}`,
    {
      method: 'GET',
      headers: getHeaders(),
    }
  );
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to fetch assignment detail');
  }
  return response.json();
};

export const fetchMySubmission = async (
  assignmentId: string
): Promise<StudentSubmissionResponse | null> => {
  const response = await fetch(
    `${API_BASE}/student/assignments/${assignmentId}/my-submission`,
    {
      method: 'GET',
      headers: getHeaders(),
    }
  );
  if (!response.ok) {
    if (response.status === 404) {
      return null;
    }
    const error = await response.json();
    throw new Error(error.message || 'Failed to fetch submission');
  }
  return response.json();
};

export const submitAssignment = async (
  assignmentId: string,
  dto: SubmitAssignmentDto
): Promise<StudentSubmissionResponse> => {
  const response = await fetch(
    `${API_BASE}/student/assignments/${assignmentId}/submit`,
    {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(dto),
    }
  );
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to submit assignment');
  }
  return response.json();
};

export const updateSubmission = async (
  submissionId: string,
  dto: UpdateSubmissionDto
): Promise<StudentSubmissionResponse> => {
  const response = await fetch(
    `${API_BASE}/student/submissions/${submissionId}`,
    {
      method: 'PATCH',
      headers: getHeaders(),
      body: JSON.stringify(dto),
    }
  );
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to update submission');
  }
  return response.json();
};

// ==================== FILE UPLOAD ====================

export interface UploadedFileResponse {
  success: boolean;
  file: {
    name: string;
    fileName: string;
    url: string;
    type: string;
    fileType: string;
    size: number;
    fileSize: number;
  };
}

/**
 * Upload a file to the server for student submission
 * Returns a persistent server URL
 */
/** Longest time an upload may take before the student is told to retry. */
const UPLOAD_TIMEOUT_MS = 60_000;

export const uploadSubmissionFile = async (file: File): Promise<UploadedFileResponse> => {
  const tokens = useAuthStore.getState().tokens;
  const accessToken = tokens?.accessToken;

  const formData = new FormData();
  formData.append('file', file);

  let response: Response;
  try {
    response = await fetch(`${API_BASE}/student/upload-file`, {
      method: 'POST',
      headers: {
        Authorization: accessToken ? `Bearer ${accessToken}` : '',
        // Don't set Content-Type - let the browser set it with the boundary
      },
      body: formData,
      // A stalled upload must fail with a message, never spin forever
      signal: AbortSignal.timeout(UPLOAD_TIMEOUT_MS),
    });
  } catch (err) {
    if (err instanceof DOMException && (err.name === 'TimeoutError' || err.name === 'AbortError')) {
      throw new Error('Le téléversement a pris trop de temps. Vérifiez votre connexion et réessayez.');
    }
    throw err;
  }

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to upload file');
  }

  return response.json();
};

// ==================== EXAM SCHEDULE ====================

/**
 * Get student's exam schedule for the semester
 */
export const fetchMyExamSchedule = async (
  academicYearId?: string,
  semester?: number
): Promise<StudentExamScheduleResponse> => {
  const params = new URLSearchParams();
  if (academicYearId) params.append('academicYearId', academicYearId);
  if (semester !== undefined) params.append('semester', String(semester));

  const url = `${API_BASE}/student/exam-schedule${params.toString() ? `?${params.toString()}` : ''}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: getHeaders(),
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to fetch exam schedule');
  }
  return response.json();
};

// ==================== RATTRAPAGE SCHEDULE ====================

/**
 * Get student's rattrapage (make-up exam) schedule
 */
export const fetchMyRattrapageSchedule = async (
  academicYearId?: string,
  semester?: number
): Promise<StudentRattrapageScheduleResponse> => {
  const params = new URLSearchParams();
  if (academicYearId) params.append('academicYearId', academicYearId);
  if (semester !== undefined) params.append('semester', String(semester));

  const url = `${API_BASE}/student/rattrapage-schedule${params.toString() ? `?${params.toString()}` : ''}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: getHeaders(),
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to fetch rattrapage schedule');
  }
  return response.json();
};

/**
 * Get student's rattrapage history
 */
export const fetchMyRattrapageHistory = async (
  academicYearId?: string
): Promise<StudentRattrapageHistoryResponse> => {
  const params = new URLSearchParams();
  if (academicYearId) params.append('academicYearId', academicYearId);

  const url = `${API_BASE}/student/rattrapage-history${params.toString() ? `?${params.toString()}` : ''}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: getHeaders(),
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to fetch rattrapage history');
  }
  return response.json();
};
